import React, { useState, useMemo } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Checkbox, 
  Box, 
  Typography, 
  Button, 
  IconButton, 
  TextField, 
  Skeleton,
  Stack,
  Card,
  CardContent,
  CardActions,
  Divider,
  Tooltip,
  Pagination,
  MenuItem
} from '@mui/material';
import { 
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { getTestId } from '../../../utils/testUtils';

// Skeleton loader for table rows
const TableRowSkeleton = ({ columns, hasSelection, hasActions }) => {
  return (
    <TableRow>
      {hasSelection && (
        <TableCell padding="checkbox">
          <Skeleton variant="rectangular" width={24} height={24} />
        </TableCell>
      )}
      {Array.from({ length: columns.length }, (_, index) => (
        <TableCell key={index}>
          <Skeleton variant="text" width="80%" />
        </TableCell>
      ))}
      {hasActions && (
        <TableCell align="right">
          <Skeleton variant="rectangular" width={60} height={24} />
        </TableCell>
      )}
    </TableRow>
  );
};

// Mobile card component for responsive display
const MobileCard = ({ row, columns, selection, actions, onRowSelect, selectedRows }) => {
  const rowId = row[selection?.idField || 'id'];
  const isSelected = selectedRows.has(rowId);

  return (
    <Card 
      variant="outlined" 
      sx={{
        mb: 2,
        border: isSelected ? 2 : 1,
        borderColor: isSelected ? 'primary.main' : 'divider'
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        {selection?.enabled && (
          <Box sx={{ mb: 1 }}>
            <Checkbox
              checked={isSelected}
              onChange={(e) => onRowSelect(rowId, e.target.checked)}
              size="small"
            />
          </Box>
        )}
        
        <Stack spacing={1.5}>
          {columns.map(column => {
            let value = column.render ? column.render(row[column.key], row) : row[column.key];
            
            // Safety check: ensure we don't render objects as React children
            if (value && typeof value === 'object' && !React.isValidElement(value)) {
              if (value.message) value = value.message;
              else if (value.description) value = value.description;
              else if (value.msg) value = value.msg;
              else value = JSON.stringify(value);
            }
            
            return (
              <Box key={column.key}>
                <Typography variant="caption" color="text.secondary">
                  {column.title}
                </Typography>
                <Box>
                  {value}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </CardContent>

      {actions?.row && actions.row.length > 0 && (
        <>
          <Divider />
          <CardActions sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
            {actions.row.map((action, actionIndex) => (
              <Button
                key={actionIndex}
                size="small"
                variant={action.variant === 'ghost' ? 'text' : 'contained'}
                onClick={() => action.onClick(row)}
                disabled={action.disabled?.(row)}
                color={action.variant === 'danger' ? 'error' : 
                      action.variant === 'success' ? 'success' : 'primary'}
                startIcon={typeof action.icon === 'function' ? action.icon(row) : action.icon}
              >
                {action.label || action.title}
              </Button>
            ))}
          </CardActions>
        </>
      )}
    </Card>
  );
};

/**
 * DataTable component - A fully featured data table with sorting, filtering, pagination, and responsive design
 */
const DataTable = ({
  data = [],
  columns = [],
  loading = false,
  error = null,
  pagination = null,
  sorting = null,
  filtering = null,
  selection = null,
  actions = null,
  emptyState = null,
  forceTableOnMobile = false,
  sx = {},
  ...props
}) => {
  // State for selected rows
  const [selectedRows, setSelectedRows] = useState(new Set());
  
  // State for sorting
  const [sortConfig, setSortConfig] = useState(null);
  
  // State for filters
  const [filters] = useState({});
  

  // Apply filters to data
  const filteredData = useMemo(() => {
    if (!filtering?.enabled || Object.keys(filters).length === 0) return data;
    
    return data.filter(row => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        const cellValue = row[key];
        if (typeof cellValue === 'string') {
          return cellValue.toLowerCase().includes(value.toLowerCase());
        }
        return cellValue === value;
      });
    });
  }, [data, filters, filtering]);

  // Apply sorting to filtered data
  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === bValue) return 0;
      
      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      const comparison = aValue < bValue ? -1 : 1;
      return sortConfig.direction === 'desc' ? comparison * -1 : comparison;
    });
  }, [filteredData, sortConfig]);

  // Handle sorting
  const handleSort = (columnKey) => {
    if (!sorting?.enabled) return;

    setSortConfig(prevConfig => {
      if (prevConfig?.key === columnKey) {
        if (prevConfig.direction === 'asc') {
          return { key: columnKey, direction: 'desc' };
        } else if (prevConfig.direction === 'desc') {
          return null; // Remove sorting
        }
      }
      return { key: columnKey, direction: 'asc' };
    });
  };

  // Handle row selection
  const handleRowSelect = (rowId, checked) => {
    if (!selection?.enabled) return;
    
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(rowId);
      } else {
        newSet.delete(rowId);
      }
      return newSet;
    });
  };

  // Handle select all
  const handleSelectAll = (checked) => {
    if (!selection?.enabled) return;
    
    if (checked) {
      const allIds = sortedData.map(row => row[selection.idField || 'id']);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };


  // Get cell content
  const getCellContent = (row, column) => {
    let value;
    if (column.render) {
      value = column.render(row[column.key], row);
    } else {
      value = row[column.key];
    }
    
    // Safety check: ensure we don't render objects as React children
    if (value && typeof value === 'object' && !React.isValidElement(value)) {
      // If it's an object with message or description, extract the string
      if (value.message) return value.message;
      if (value.description) return value.description;
      if (value.msg) return value.msg;
      // For other objects, safely stringify
      return JSON.stringify(value);
    }
    
    return value;
  };

  // Get sort icon
  const getSortIcon = (columnKey) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return null;
    }
    return sortConfig.direction === 'asc' 
      ? <KeyboardArrowUpIcon fontSize="small" />
      : <KeyboardArrowDownIcon fontSize="small" />;
  };

  const isAllSelected = selectedRows.size > 0 && selectedRows.size === sortedData.length && sortedData.length > 0;
  const isIndeterminate = selectedRows.size > 0 && selectedRows.size < sortedData.length;


  return (
    <Box 
      {...getTestId('data-table')}
      sx={{ width: '100%', ...sx }} 
      {...props}
    >

      {/* Bulk Actions */}
      {selection?.enabled && selectedRows.size > 0 && actions?.bulk && (
        <Paper sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">
            <strong>{selectedRows.size}</strong> {selectedRows.size === 1 ? 'item' : 'items'} selected
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {actions.bulk.map((action, index) => (
              <Button
                key={index}
                size="small"
                variant={action.variant === 'ghost' ? 'text' : 'contained'}
                onClick={() => action.onClick(Array.from(selectedRows))}
                disabled={action.disabled}
                color={action.variant === 'danger' ? 'error' : 
                       action.variant === 'success' ? 'success' : 'primary'}
                startIcon={action.icon}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        </Paper>
      )}

      {/* Error State */}
      {error && (
        <Paper 
          sx={{ 
            p: 2, 
            mb: 2, 
            bgcolor: 'error.light', 
            color: 'error.contrastText',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
          role="alert"
        >
          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
            ⚠️ {error}
          </Typography>
        </Paper>
      )}

      {/* Table and Card Views with Responsive Display */}
      {!error && (
        <>
          {/* Desktop Table View - Only shown on medium screens and up, or when forced on mobile */}
          <Box sx={{ display: { xs: forceTableOnMobile ? 'block' : 'none', md: 'block' } }}>
            <TableContainer 
              {...getTestId('data-table-container')}
              component={Paper} 
              sx={{ boxShadow: 1, borderRadius: 1, overflow: 'hidden' }}
            >
              <Table {...getTestId('data-table-table')}>
                <TableHead {...getTestId('data-table-head')}>
                  <TableRow {...getTestId('data-table-header-row')}>
                    {selection?.enabled && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          {...getTestId('data-table-select-all')}
                          checked={isAllSelected}
                          indeterminate={isIndeterminate}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          disabled={loading}
                        />
                      </TableCell>
                    )}
                    {columns.map(column => (
                      <TableCell
                        {...getTestId(`data-table-header-${column.key}`)}
                        key={column.key}
                        align={column.align || 'left'}
                        sortDirection={sortConfig?.key === column.key ? sortConfig.direction : false}
                        onClick={() => !loading && sorting?.enabled && column.sortable && handleSort(column.key)}
                        sx={{
                          width: column.width,
                          cursor: sorting?.enabled && column.sortable ? 'pointer' : 'default',
                          fontWeight: 'bold',
                          '&:hover': {
                            backgroundColor: sorting?.enabled && column.sortable ? 'action.hover' : 'inherit'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: column.align === 'right' ? 'flex-end' : 'flex-start' }}>
                          {column.title}
                          {sorting?.enabled && column.sortable && (
                            <Box component="span" sx={{ ml: 0.5, display: 'flex', alignItems: 'center' }}>
                              {getSortIcon(column.key)}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    ))}
                    {actions?.row && (
                      <TableCell 
                        {...getTestId('data-table-actions-header')}
                        align="right"
                      >
                        Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody {...getTestId('data-table-body')}>
                  {loading ? (
                    // Skeleton loading rows
                    Array.from({ length: pagination?.pageSize || 5 }, (_, index) => (
                      <TableRowSkeleton 
                        key={`skeleton-${index}`}
                        columns={columns}
                        hasSelection={selection?.enabled}
                        hasActions={Boolean(actions?.row)}
                      />
                    ))
                  ) : sortedData.length === 0 ? (
                    <TableRow>
                      <TableCell 
                        colSpan={
                          columns.length + 
                          (selection?.enabled ? 1 : 0) + 
                          (actions?.row ? 1 : 0)
                        }
                      >
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                          {(() => {
                            // Render empty state safely for different types
                            if (!emptyState) {
                              return <Typography color="text.secondary">No data available</Typography>;
                            }
                            if (React.isValidElement(emptyState)) {
                              return emptyState;
                            }
                            if (typeof emptyState === 'string') {
                              return <Typography color="text.secondary">{emptyState}</Typography>;
                            }
                            if (typeof emptyState === 'object') {
                              const message = emptyState.message || emptyState.title;
                              const description = emptyState.description || emptyState.subtitle || emptyState.detail;
                              return (
                                <Box>
                                  {message && (
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                      {message}
                                    </Typography>
                                  )}
                                  {description && (
                                    <Typography color="text.secondary">
                                      {description}
                                    </Typography>
                                  )}
                                  {!message && !description && (
                                    <Typography color="text.secondary">No data available</Typography>
                                  )}
                                </Box>
                              );
                            }
                            return <Typography color="text.secondary">No data available</Typography>;
                          })()}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedData.map((row, index) => {
                      const rowId = row[selection?.idField || 'id'];
                      const isSelected = selectedRows.has(rowId);
                      
                      return (
                        <TableRow 
                          key={rowId || index} 
                          selected={isSelected}
                          hover
                          sx={{
                            '&.Mui-selected': {
                              backgroundColor: 'primary.lighter',
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: 'primary.lighter',
                            }
                          }}
                        >
                          {selection?.enabled && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isSelected}
                                onChange={(e) => handleRowSelect(rowId, e.target.checked)}
                              />
                            </TableCell>
                          )}
                          {columns.map(column => (
                            <TableCell
                              key={column.key}
                              align={column.align || 'left'}
                            >
                              {getCellContent(row, column)}
                            </TableCell>
                          ))}
                          {actions?.row && (
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                {actions.row.map((action, actionIndex) => {
                                  const title = typeof action.title === 'function' ? action.title(row) : (action.title || action.label || '');
                                  const variant = typeof action.variant === 'function' ? action.variant(row) : action.variant;
                                  return (
                                  <Tooltip key={actionIndex} title={title}>
                                    <span> {/* Wrapper for disabled buttons */}
                                      <IconButton
                                        size="small"
                                        onClick={() => action.onClick(row)}
                                        disabled={action.disabled?.(row)}
                                        color={variant === 'danger' ? 'error' :
                                              variant === 'success' ? 'success' : 'default'}
                                        aria-label={title}
                                      >
                                        {typeof action.icon === 'function' ? action.icon(row) : action.icon}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  );
                                })}
                              </Stack>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Mobile Card View - Only shown on small screens when not forcing table view */}
          {!forceTableOnMobile && (
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              {loading ? (
                // Mobile skeleton loading
                <Stack spacing={2}>
                  {Array.from({ length: pagination?.pageSize || 5 }, (_, index) => (
                    <Paper key={`mobile-skeleton-${index}`} sx={{ p: 2 }}>
                      <Skeleton variant="rectangular" height={100} />
                    </Paper>
                  ))}
                </Stack>
              ) : sortedData.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  {(() => {
                    if (!emptyState) {
                      return <Typography color="text.secondary">No data available</Typography>;
                    }
                    if (React.isValidElement(emptyState)) {
                      return emptyState;
                    }
                    if (typeof emptyState === 'string') {
                      return <Typography color="text.secondary">{emptyState}</Typography>;
                    }
                    if (typeof emptyState === 'object') {
                      const message = emptyState.message || emptyState.title;
                      const description = emptyState.description || emptyState.subtitle || emptyState.detail;
                      return (
                        <Box>
                          {message && (
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              {message}
                            </Typography>
                          )}
                          {description && (
                            <Typography color="text.secondary">
                              {description}
                            </Typography>
                          )}
                          {!message && !description && (
                            <Typography color="text.secondary">No data available</Typography>
                          )}
                        </Box>
                      );
                    }
                    return <Typography color="text.secondary">No data available</Typography>;
                  })()}
                </Box>
              ) : (
                <Stack spacing={2}>
                  {sortedData.map((row, index) => (
                    <MobileCard
                      key={row[selection?.idField || 'id'] || index}
                      row={row}
                      columns={columns}
                      selection={selection}
                      actions={actions}
                      onRowSelect={handleRowSelect}
                      selectedRows={selectedRows}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          )}

        </>
      )}

      {/* Pagination */}
      {pagination && !loading && !error && sortedData.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {((pagination.page - 1) * pagination.pageSize) + 1} to{' '}
            {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{' '}
            {pagination.total} entries
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Rows per page:
              </Typography>
              <TextField
                select
                size="small"
                value={pagination.pageSize}
                onChange={(e) => pagination.onPageSizeChange(parseInt(e.target.value))}
                variant="outlined"
                sx={{ minWidth: 80 }}
              >
                {(pagination.pageSizeOptions || [10, 25, 50, 100]).map(size => (
                  <MenuItem key={size} value={size}>{size}</MenuItem>
                ))}
              </TextField>
            </Box>
            
            <Pagination
              count={Math.ceil(pagination.total / pagination.pageSize)}
              page={pagination.page}
              onChange={(e, newPage) => pagination.onPageChange(newPage)}
              color="primary"
              shape="rounded"
              size="medium"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DataTable;